import React, { FunctionComponent, useMemo, useState } from "react";
import { PaymentMethods } from "@coinflowlabs/react";
import { Stack } from "react-bootstrap";
import {
  PaymentProvider,
  PaymentProviderStatus,
  PaymentType,
  PurchasePlan,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";
import "./CompleteUsdTopup.scss";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import {
  GenericModalProps,
  ThemeVariant,
} from "../../../utils/constants/generic-types";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import TopupBundle from "../TopupBundle/TopupBundle";
import { checkOs, formatCurrency } from "../../../utils/helpers";
import GooglePayIcon from "../../icons/GooglePayIcon";
import { OperatingSystems } from "../../../utils/vendor";
import ApplePayIcon from "../../icons/ApplePayIcon";
import PaypalIcon from "../../icons/PaypalIcon";
import CardIcon from "../../icons/CardIcon";
import MastercardIcon from "../../icons/MastercardIcon";
import VisaIcon from "../../icons/VisaIcon";
import GhostButton from "../../dashboard-page/GhostButton/GhostButton";
import ProcessingFeeInfo from "../ProcessingFeeInfo/ProcessingFeeInfo";
import useDisclosure from "../../../hooks/useDisclosure";
import usePaymentProvidersQuery from "../../../react-query/queries/use-payment-providers";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

interface CompleteUsdTopupProps extends GenericModalProps {
  isBestDeal?: boolean;
  metadataObj?: Record<string, number>;
  selectedPurchasePlan: PurchasePlan;
  selectedPurchasePlanIsSpecial?: boolean;
  handleInitPayment: (paymentProvider?: string, paymentMethod?: string) => void;
}

type SupportedPaymentTypes = Exclude<
  PaymentType,
  PaymentType.ACH | PaymentType.PAYSTACK_CARD | PaymentType.WALLET
>;

type UsdPaymentMethodDetails = Record<
  SupportedPaymentTypes,
  {
    Icon: FunctionComponent;
    provider: PaymentProvider;
    method?: PaymentMethods;
  }
>;

const usdPaymentMethodDetails: UsdPaymentMethodDetails = {
  [PaymentType.APPLE_PAY]: {
    Icon: ApplePayIcon,
    provider: PaymentProvider.COINFLOW,
    method: PaymentMethods.applePay,
  },
  [PaymentType.GOOGLE_PAY]: {
    Icon: GooglePayIcon,
    provider: PaymentProvider.COINFLOW,
    method: PaymentMethods.googlePay,
  },
  [PaymentType.PAYPAL]: {
    Icon: PaypalIcon,
    provider: PaymentProvider.PAYPAL,
    method: undefined,
  },
  [PaymentType.CARD]: {
    Icon: CardIcon,
    provider: PaymentProvider.COINFLOW,
    method: PaymentMethods.card,
  },
};

// Unhide the other card provider icons when we include support for them
const CARD_PROVIDER_ICONS = [
  MastercardIcon,
  VisaIcon,
  // AmexIcon,
  // DiscoverIcon,
  // JcbIcon,
];

export default function CompleteUsdTopup({
  isOpen,
  onClose,
  isBestDeal,
  metadataObj,
  selectedPurchasePlan,
  selectedPurchasePlanIsSpecial,
  handleInitPayment,
}: CompleteUsdTopupProps) {
  const os = checkOs();
  const { isFiatDisplay } = useHprFiatFormatter();
  const { t } = useTranslation("wallet");
  const { price, sourceCurrency, processingFees } = selectedPurchasePlan;
  const {
    isOpen: isProcessingFeeInfoOpen,
    onOpen: openProcessingFeeInfo,
    onClose: closeProcessingFeeInfo,
  } = useDisclosure();
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<SupportedPaymentTypes>();
  const { paymentProviders } = usePaymentProvidersQuery();

  const selectedPaymentMethodDetails = useMemo(() => {
    if (!selectedPaymentMethod) return;
    return usdPaymentMethodDetails[selectedPaymentMethod];
  }, [selectedPaymentMethod]);

  const processingFee = useMemo(
    () =>
      processingFees?.find((item) => item.paymentType === selectedPaymentMethod)
        ?.amount ?? 0,
    [processingFees, selectedPaymentMethod],
  );

  const availableUsdPaymentMethods = useMemo(() => {
    const availableMethods = Object.keys(usdPaymentMethodDetails).filter(
      (method): method is SupportedPaymentTypes => {
        if (os === OperatingSystems.MacOs) return true;
        if (os === OperatingSystems.Ios) {
          return method !== PaymentType.GOOGLE_PAY;
        }
        return method !== PaymentType.APPLE_PAY;
      },
    );

    return availableMethods;
  }, [os]);

  const isPaymentProviderInactive = (provider: PaymentProvider) => {
    const providerStatus = paymentProviders?.find(
      (paymentProvider) => paymentProvider.name === provider,
    )?.status;
    return providerStatus === PaymentProviderStatus.INACTIVE;
  };

  const buttonDetails = useMemo(() => {
    let text = t("pay_amount", {
      amount: formatCurrency(price + processingFee, sourceCurrency, 2),
    });
    let rightImage;
    switch (selectedPaymentMethod) {
      case undefined:
        break;
      case PaymentType.CARD:
        text += ` ${t("with_card")}`;
        break;
      default:
        text += ` ${t("with")}`;
        rightImage = selectedPaymentMethodDetails?.Icon({
          variant: ThemeVariant.LIGHT,
        });
    }
    return { text, rightImage };
  }, [
    t,
    price,
    processingFee,
    sourceCurrency,
    selectedPaymentMethod,
    selectedPaymentMethodDetails,
  ]);

  const resetSelectedPaymentMethod = () => {
    setSelectedPaymentMethod(undefined);
  };

  const handleClose = () => {
    resetSelectedPaymentMethod();
    onClose();
  };

  return (
    <>
      <BottomDrawer
        isOpen={isOpen}
        onClose={handleClose}
        containerClass="CompleteUsdTopupModal"
      >
        <div className="CompleteUsdTopupContent">
          <Stack gap={4}>
            <Stack gap={1} className="Copy">
              <h3 className="text-capitalize Hanson-700 Title">
                {t("complete_top_up")}
              </h3>
              <p className="Grotesk Description">
                {isFiatDisplay
                  ? t("select_a_payment_method_to_complete_top_up")
                  : t("complete_top_up_and_receive_your_hpr_in_minutes")}
              </p>
            </Stack>
            <Stack gap={2} className="SelectedPurchasePlan">
              <h4 className="Grotesk SpecialFontSettings Title">
                {isFiatDisplay ? t("bundle") : t("hpr_bundle")}
              </h4>
              <TopupBundle
                isStandalone
                isBestDeal={isBestDeal}
                metadataObj={metadataObj}
                plan={selectedPurchasePlan}
                isSpecialOffer={selectedPurchasePlanIsSpecial}
              />
            </Stack>
            <Stack className="PaymentMethods">
              <h4 className="text-capitalize Grotesk SpecialFontSettings Title">
                {t("payment_method")}
              </h4>
              <Stack direction="horizontal" gap={2}>
                {availableUsdPaymentMethods.map((option) => {
                  const { Icon: PaymentOptionIcon, provider } =
                    usdPaymentMethodDetails[option];
                  const providerIsInactive =
                    isPaymentProviderInactive(provider);
                  return (
                    <button
                      key={option}
                      disabled={providerIsInactive}
                      className={`PaymentMethod ${
                        option === selectedPaymentMethod ? "Selected" : ""
                      } ${providerIsInactive ? "Unavailable" : ""}`}
                      onClick={() => setSelectedPaymentMethod(option)}
                    >
                      <PaymentOptionIcon />
                      {providerIsInactive && <UnavailableTag />}
                    </button>
                  );
                })}
              </Stack>
              {selectedPaymentMethod === PaymentType.CARD && (
                <Stack direction="horizontal" gap={2}>
                  <p className="Grotesk SpecialFontSettings AcceptedCards">
                    {t("accepted_cards")}
                    {t("common:punctuations.colon")}
                  </p>
                  {CARD_PROVIDER_ICONS.map((CardProviderIcon, index) => (
                    <CardProviderIcon key={index} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>

          <Stack gap={3} style={{ maxHeight: "max-content" }}>
            {!!processingFee && (
              <GhostButton
                className="Grotesk SpecialFontSettings ProcessingFee"
                onClick={openProcessingFeeInfo}
              >
                {t("processing_fee")}:
                <span className="ProcessingFeeAmount">
                  {formatCurrency(processingFee, sourceCurrency, 2)}
                </span>
              </GhostButton>
            )}
            <PrimaryActionButton
              size="lg"
              textClassName="PayCtaText"
              disabled={!selectedPaymentMethod}
              text={buttonDetails.text}
              rightImage={buttonDetails.rightImage}
              onClick={() =>
                handleInitPayment(
                  selectedPaymentMethodDetails?.provider,
                  selectedPaymentMethodDetails?.method,
                )
              }
            />
          </Stack>
        </div>
      </BottomDrawer>

      <ProcessingFeeInfo
        isOpen={isProcessingFeeInfoOpen}
        onClose={closeProcessingFeeInfo}
      />
    </>
  );
}

function UnavailableTag() {
  const { t } = useTranslation("wallet");
  return (
    <p className="d-flex justify-content-center align-items-center text-capitalize UnavailableTag">
      {t("unavailable")}
    </p>
  );
}
